import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, LineChart, Line } from "recharts";
import {
  ArrowDownRight, ArrowRight, ArrowUpRight, CreditCard, XCircle, RotateCcw, UserMinus, UserPlus,
  Sparkles, MessageSquareWarning, LifeBuoy, TrendingUp, TrendingDown, Clock,
} from "lucide-react";
import {
  changeTone, formatChange, formatChangePct, type ChangeTone, type MetricUnit, type SeriesPoint,
} from "@/lib/metrics";

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2" data-testid="metrics-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-8 ${c === 0 ? "flex-[3]" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardsSkeleton({ count = 8, className = "h-32" }: { count?: number; className?: string }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => <Skeleton key={i} className={className} />)}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint: string; action?: React.ReactNode }) {
  return (
    <div className="text-center py-10 space-y-2" data-testid="metrics-empty">
      <p className="font-medium">{title}</p>
      <p className="text-sm text-muted-foreground max-w-lg mx-auto">{hint}</p>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, hint }: { error: unknown; hint?: string }) {
  return (
    <div className="text-center py-10 space-y-1" data-testid="metrics-error">
      <p className="font-medium text-destructive">Could not load this data</p>
      <p className="text-sm text-muted-foreground">{(error as Error)?.message || "Unknown error"}</p>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** Compact stat tile matching the Revenue page. */
export function Tile({
  label, value, sub, tone, icon, testId,
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  tone?: "good" | "warn" | "bad";
  icon?: React.ReactNode;
  testId?: string;
}) {
  const border =
    tone === "warn" ? "border-amber-400/70 bg-amber-50/50 dark:bg-amber-950/20"
      : tone === "bad" ? "border-destructive/60 bg-destructive/5"
        : tone === "good" ? "border-green-400/60 bg-green-50/40 dark:bg-green-950/20"
          : "bg-card";
  return (
    <div className={`rounded-lg border p-3 ${border}`} data-testid={testId}>
      <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{label}</span>{icon}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
    </div>
  );
}

const TONE_CLASS: Record<ChangeTone, string> = {
  good: "text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  bad: "text-destructive bg-destructive/10 border-destructive/30",
  flat: "text-muted-foreground bg-muted border-transparent",
};

/** Green when the change is good for the business (down is good for churn, fees, refunds …). */
export function ChangeChip({
  metricId, change, changePct, unit, className = "",
}: {
  metricId: string;
  change: number | null | undefined;
  changePct: number | null | undefined;
  unit: MetricUnit;
  className?: string;
}) {
  const tone = changeTone(metricId, change);
  const pct = formatChangePct(changePct);
  const Icon = tone === "flat" ? ArrowRight : (change ?? 0) > 0 ? ArrowUpRight : ArrowDownRight;
  if (change == null) {
    return <span className={`text-xs text-muted-foreground ${className}`}>no previous period</span>;
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums ${TONE_CLASS[tone]} ${className}`}
      data-testid={`change-${metricId}`}
    >
      <Icon className="w-3 h-3" />
      {formatChange(change, unit)}
      {pct ? <span className="opacity-70">({pct})</span> : null}
    </span>
  );
}

/** Axis-free sparkline. */
export function Sparkline({ points, tone = "flat" }: { points: SeriesPoint[]; tone?: ChangeTone }) {
  if (!points || points.length < 2) return <div className="h-10" />;
  const stroke = tone === "good" ? "#16a34a" : tone === "bad" ? "hsl(var(--destructive))" : "hsl(var(--primary))";
  return (
    <div className="h-10 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey="value" stroke={stroke} strokeWidth={1.75} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const TIMELINE_STYLE: Record<string, { icon: React.ElementType; className: string }> = {
  payment: { icon: CreditCard, className: "text-emerald-600 bg-emerald-500/10" },
  payment_pending: { icon: Clock, className: "text-muted-foreground bg-muted" },
  payment_failed: { icon: XCircle, className: "text-destructive bg-destructive/10" },
  refund: { icon: RotateCcw, className: "text-amber-600 bg-amber-500/10" },
  churn: { icon: UserMinus, className: "text-muted-foreground bg-muted" },
  new: { icon: UserPlus, className: "text-blue-600 bg-blue-500/10" },
  reactivation: { icon: UserPlus, className: "text-blue-600 bg-blue-500/10" },
  expansion: { icon: TrendingUp, className: "text-emerald-600 bg-emerald-500/10" },
  contraction: { icon: TrendingDown, className: "text-amber-600 bg-amber-500/10" },
  trial_start: { icon: Sparkles, className: "text-purple-600 bg-purple-500/10" },
  trial_end_no_convert: { icon: Sparkles, className: "text-purple-600 bg-purple-500/10" },
  exit_survey: { icon: MessageSquareWarning, className: "text-muted-foreground bg-muted" },
  recovery: { icon: LifeBuoy, className: "text-sky-600 bg-sky-500/10" },
};

export function timelineStyle(kind: string) {
  return TIMELINE_STYLE[kind] || { icon: ArrowRight, className: "text-muted-foreground bg-muted" };
}

const ANNOTATION_VARIANT: Record<string, string> = {
  campaign: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  pricing: "bg-purple-500/10 text-purple-700 dark:text-purple-300",
  product: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  exam: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  incident: "bg-destructive/10 text-destructive",
  note: "bg-muted text-muted-foreground",
};

export function AnnotationBadge({ kind }: { kind: string }) {
  return <Badge className={`border-0 capitalize ${ANNOTATION_VARIANT[kind] || ANNOTATION_VARIANT.note}`}>{kind}</Badge>;
}

const STATUS_CLASS: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  trialing: "bg-purple-500/10 text-purple-700 dark:text-purple-300",
  past_due: "bg-destructive/10 text-destructive",
  churned: "bg-muted text-muted-foreground",
  canceled: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
  paused: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  open: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  recovered: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  lost: "bg-destructive/10 text-destructive",
  closed: "bg-muted text-muted-foreground",
};

export function StatusPill({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-xs text-muted-foreground">–</span>;
  const cls = STATUS_CLASS[status] || "bg-muted text-muted-foreground";
  return <Badge className={`border-0 ${cls}`}>{status.replace(/_/g, " ")}</Badge>;
}
